<template>
    <div :class="{ 'keen-address-container': true, 'has-icon': icon.length !== 0 }">
        <div class="icon-holder">
            <span class="material-icons" v-html="icon"></span>
        </div>
        <div class="address-holder">
            <div :class="searchClasses">
                <div class="col">
                    <div class="ui-textbox__content">
                        <label class="ui-textbox__label">
                            <span v-if="label.length > 0" class="ui-textbox__label-text">{{ label }}</span>
                            <input :id="'pcaQuery' + id" :name="'pcaQuery' + id" placeholder="Start typing a postcode, street or address" type="text" class="ui-textbox__input">
                        </label>
                        <div class="ui-textbox__feedback">
                            <div class="ui-textbox__feedback-text">
                                <a href="#" @click.prevent="manualEntry">Enter address manually</a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div :class="entryClasses">
                    <div class="row">
                        <div class="col">
                            <ui-textbox
                                id="address_1"
                                v-model="localAddress.address_1"
                                placeholder="Address Line 1"
                                @input="emitUpdated"
                                :invalid="hasErrors(errors, ['address_1'])"
                                :error="firstError(errors, ['address_1'])"
                            ></ui-textbox>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col">
                            <ui-textbox
                                id="address_2"
                                v-model="localAddress.address_2"
                                placeholder="Address Line 2"
                                @input="emitUpdated"
                                :invalid="hasErrors(errors, ['address_2'])"
                                :error="firstError(errors, ['address_2'])"
                            ></ui-textbox>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col">
                            <ui-textbox
                                id="address_3"
                                v-model="localAddress.address_3"
                                placeholder="Address Line 3"
                                @input="emitUpdated"
                                :invalid="hasErrors(errors, ['address_3'])"
                                :error="firstError(errors, ['address_3'])"
                            ></ui-textbox>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col">
                            <ui-textbox
                                id="town"
                                v-model="localAddress.town"
                                placeholder="Town"
                                @input="emitUpdated"
                                :invalid="hasErrors(errors, ['town'])"
                                :error="firstError(errors, ['town'])"
                            ></ui-textbox>
                        </div>
                        <div class="col">
                            <ui-textbox
                                id="county"
                                v-model="localAddress.county"
                                placeholder="County"
                                @input="emitUpdated"
                                :invalid="hasErrors(errors, ['county'])"
                                :error="firstError(errors, ['county'])"
                            ></ui-textbox>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col">
                            <ui-textbox
                                id="post_code"
                                v-model="localAddress.post_code"
                                placeholder="Post Code"
                                @input="emitUpdated"
                                :invalid="hasErrors(errors, ['post_code'])"
                                :error="firstError(errors, ['post_code'])"
                            ></ui-textbox>
                        </div>
                        <div class="col">
                            <ui-select id="country"
                               has-search
                               v-model="countryProxy"
                               placeholder="Country"
                               :options="countries"
                               @input="emitUpdated"
                                :invalid="hasErrors(errors, ['country'])"
                                :error="firstError(errors, ['country'])"
                            ></ui-select>
                        </div>
                    </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
    .keen-address-container {
        display: flex;
    }

    .icon-holder { display: none; }
    .has-icon {
        .icon-holder { display: block; flex-basis: 30px; color: rgba(0, 0, 0, 0.54); margin-right: 5px; margin-top: 5px; }
    }

    .address-holder { flex-grow: 1; }

    .address-fields-hidden { display: none; }
</style>

<script>
    import VueInput from './Input.vue';
    import VueSelect from './Select.vue';
    import Countries from '../../../../../data/countries.json'
    import {firstError, hasErrors} from "../../../utils";

    let _resolvePca;
    let _pcaInstance;

    export default {
        components: { VueInput, VueSelect, },
        props: {
            icon: {
                type: String,
                default: ''
            },
            errors: {
                default() {
                    return {}
                }
            },
            label: {
                type: String,
                default: 'Address',
            },
            value: {
                default() {
                    return {   
                        address_1: '',
                        address_2: '',
                        address_3: '',
                        town: '',
                        county: '',
                        post_code: '',
                        country: '',
                    }
                }
            }
        },
        data() {
            return {
                id: '',
                localAddress: {   
                    address_1: '',
                    address_2: '',
                    address_3: '',
                    town: '',
                    county: '',
                    post_code: '',
                    country: '',
                },
                manual_entry: false,
            }
        },
        created() {
            this.id = this.guid();

            this.loadPca()
        },
        mounted() {
            let context = this; 

            if (!_resolvePca) {
                this.loadPca()
            }

            _resolvePca.then((pca) => {

                let fieldBindings = [
                    {
                        element: 'pcaQuery' + this.id,
                        field: '',
                        mode: pca.fieldMode.SEARCH
                    },
                ];

                _pcaInstance = new pca.Address(
                    fieldBindings, { key: 'wm25-wx24-dh48-xc88', countries: { defaultCode: window.marque.ui.localisation.country } } //this.$app.config['pca_key'] }
                );

                _pcaInstance.listen("populate", context.fillAddress);

            }, () => {
                window.handleApiError(
                    new Error('Ooops! We failed to load PCA Predict, addresses must be entered manually.')
                );
            })
        },
        beforeDestroy() {
            if (_pcaInstance) {
                _pcaInstance.destroy();
            }
            _pcaInstance = null;
            _resolvePca = null;
        },
        watch: {
            value: {
                handler: function(newVal, oldVal) {
                    if (newVal !== oldVal) {
                        this.localAddress = Object.assign({}, newVal);
                    }
                },
                deep: true,
            },
        },
        computed: {
            countries() {
                return Countries.map(item => {
                    return {
                        value: item.code,
                        label: item.name
                    }
                })
            },
            searchClasses() {
                return 'row' + (this.isPopulated || this.manual_entry ? ' address-fields-hidden' : '');
            },
            entryClasses() {
                return (this.isPopulated || this.manual_entry ? '' : 'address-fields-hidden');
            },
            isPopulated() {
                return this.localAddress.address_1 && this.localAddress.address_1.length > 0 && 
                this.localAddress.town && this.localAddress.town.length > 0 &&
                this.localAddress.county && this.localAddress.county.length > 0 &&  
                this.localAddress.post_code && this.localAddress.post_code.length > 0;
            },
            countryProxy: {
                get() {
                    let country = this.countries.find(item => {
                        return item.value === this.localAddress.country;
                    })

                    return country ? country : {};
                },
                set(value) {
                    this.localAddress.country = value.value;
                }
            }
        },
        methods: {
            firstError,
            hasErrors,
            loadPca() {
                this.requireScript('https://services.postcodeanywhere.co.uk/js/captureplus-2.20.js');
                this.requireStylesheet('https://services.postcodeanywhere.co.uk/css/captureplus-2.20.min.css');

                _resolvePca = new Promise((resolve, reject) => {
                    let expires = Date.now() + (10 * 1000);
                    let timer = setInterval(function() {
                        if (window.pca) {
                            resolve(window.pca);
                        } else if(Date.now() >= expires) {
                            clearInterval(timer);
                            reject();
                        }
                    }, 100);
                });
            },
            
            emitUpdated() {
                this.$emit('input', this.localAddress)
            },

            fillAddress(address) {
                this.$set(this, 'localAddress', {
                    address_1: address.Line1,
                    address_2: address.Line2,
                    address_3: address.Line3,
                    town: address.City,
                    county: address.Province,
                    post_code: address.PostalCode,
                    country: address.CountryIso2,
                });

                this.emitUpdated();
            },

            manualEntry() {
                this.manual_entry = true;
            },

            guid() {
                var S4 = function() {
                   return (((1+Math.random())*0x10000)|0).toString(16).substring(1);
                };
                return (S4()+S4()+"-"+S4()+"-"+S4()+"-"+S4()+"-"+S4()+S4()+S4());
            },

            reset() {
                this.localAddress = {   
                    address_1: '',
                    address_2: '',
                    address_3: '',
                    town: '',
                    county: '',
                    post_code: '',
                    country: '',
                };

                this.manual_entry = false;
            },

            requireScript(uri) {
                var head= document.getElementsByTagName('head')[0];

                if (!head.querySelector('script[src="'+uri+'"]')) {
                   var script= document.createElement('script');
                   script.type= 'text/javascript';
                   script.src= uri;
                   head.appendChild(script);
                }
            },

            requireStylesheet(uri) {
                var head= document.getElementsByTagName('head')[0];

                if (!head.querySelector('link[href="'+uri+'"]')) {
                    var sheet= document.createElement('link');
                    sheet.type= 'text/css';
                    sheet.href= uri;
                    sheet.rel='stylesheet';
                    head.appendChild(sheet);
                }
            }
        }
    }
</script>